// src/components/common/PaginationFooter.jsx
//
// Shared reusable pagination footer.
//
// PURPOSE
// -----------------------------------------------------------------------------
// This component centralizes pagination UI for table-like pages.
//
// It is intentionally generic:
// - rows-per-page selector
// - total rows / optional summary area
// - Prev / page / Next controls
// - disabled handling
//
// Page-specific files should only provide local visual skin via className,
// for example:
//   <PaginationFooter className="mp-pagination" ... />
//   <PaginationFooter className="vo-pagination" ... />
//
// IMPORTANT
// -----------------------------------------------------------------------------
// This component does NOT know whether it is used by visits, vouchers, reports,
// orders, or another table page.
//
// The generic mobile behavior lives in:
//   src/components/common/PaginationFooter.css
//
// Page CSS may override colors, borders and very small local spacing,
// but it should not rebuild the whole pagination layout.

import React from "react";
import { useTranslation } from "react-i18next";
import "./PaginationFooter.css";

export default function PaginationFooter({
  className = "",

  page = 1,
  pageCount = 1,
  pageSize = 50,
  pageSizeOptions = [10, 25, 50],
  totalRows = 0,

  disabled = false,

  onPageSizeChange,
  onPrev,
  onNext,

  /*
    Optional center summary.

    If omitted, the component displays a simple total rows text.

    This is intentionally generic so pages like VoucherOverview can pass
    a richer summary without creating a separate custom footer layout.
  */
  summary = null,

  /*
    Optional label override for simple total text.
    Example:
      totalLabel="Total: 560 vouchers"
  */
  totalLabel = null,
}) {
  const { t } = useTranslation(["common"]);

  const safePage = Number.isFinite(Number(page)) ? Number(page) : 1;
  const safePageCount = Math.max(
    1,
    Number.isFinite(Number(pageCount)) ? Number(pageCount) : 1
  );
  const safePageSize = Number.isFinite(Number(pageSize))
    ? Number(pageSize)
    : 50;
  const safeTotalRows = Number.isFinite(Number(totalRows))
    ? Number(totalRows)
    : 0;

  const rootClassName = ["pagination-footer", className]
    .filter(Boolean)
    .join(" ");

  const canPrev = !disabled && safePage > 1;
  const canNext = !disabled && safePage < safePageCount;

  return (
    <div
      className={rootClassName}
      role="navigation"
      aria-label={t("common:pagination.ariaLabel")}
    >
      <div className="pagination-footer__rows">
        <label className="pagination-footer__label">
          <span>{t("common:pagination.rows")}</span>

          <select
            value={safePageSize}
            disabled={disabled}
            onChange={(e) => {
              const next = Number(e.target.value);
              if (!Number.isFinite(next) || next <= 0) return;
              onPageSizeChange?.(next);
            }}
            aria-label={t("common:pagination.rowsPerPageAriaLabel")}
          >
            {pageSizeOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="pagination-footer__total" aria-live="polite">
        {summary || (
          <span>
            {totalLabel || t("common:pagination.total", { count: safeTotalRows })}
          </span>
        )}
      </div>

      <div className="pagination-footer__controls">
        <button
          type="button"
          className="pagination-footer__btn"
          onClick={onPrev}
          disabled={!canPrev}
          aria-label={t("common:pagination.previousPage")}
          title={t("common:pagination.previousPage")}
        >
          {t("common:pagination.previous")}
        </button>

        <span className="pagination-footer__page" aria-live="polite">
          {safePage} / {safePageCount}
        </span>

        <button
          type="button"
          className="pagination-footer__btn"
          onClick={onNext}
          disabled={!canNext}
          aria-label={t("common:pagination.nextPage")}
          title={t("common:pagination.nextPage")}
        >
          {t("common:pagination.next")}
        </button>
      </div>
    </div>
  );
}